import { Link, useParams, Navigate } from 'react-router-dom';
import { Check, ArrowRight } from 'lucide-react';
import { INDUSTRIES, EQUIPMENT, SITE } from '../data/site';
import { Seo } from '../lib/seo';
import { Reveal, SpotlightImage } from '../components/motion';
import { CTABand, PageHero } from '../components/chrome';
import { RFQForm } from '../components/RFQForm';

export default function IndustryPage() {
  const { slug } = useParams();
  const ind = INDUSTRIES.find((x) => x.slug === slug);
  if (!ind) return <Navigate to="/industries" replace />;

  const idx = INDUSTRIES.indexOf(ind);
  const crumbs = [{ label: 'Home', href: '/' }, { label: 'Industries', href: '/industries' }, { label: ind.name }];
  const serviceLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: `Analytical equipment for ${ind.name}`,
    description: ind.meta.desc,
    provider: { '@type': 'Organization', name: SITE.name, url: SITE.url },
    areaServed: 'United States (nationwide)',
  };

  const equipment = [0, 1, 4, 5].map((o) => EQUIPMENT[(idx + o) % EQUIPMENT.length]);

  return (
    <div data-testid={`page-industry-${ind.slug}`}>
      <Seo title={ind.meta.title} description={ind.meta.desc} path={`/industries/${ind.slug}`} ld={[serviceLd]} breadcrumbs={crumbs} />
      <PageHero
        eyebrow={`Industries · ${String(idx + 1).padStart(2, '0')} / ${String(INDUSTRIES.length).padStart(2, '0')}`}
        lines={ind.name}
        sub={ind.tagline}
        breadcrumbs={crumbs}
        image={ind.image}
        imageAlt={ind.alt}
      />

      <section className="section-pad">
        <div className="container-x grid lg:grid-cols-[1.1fr_0.9fr] gap-12">
          <div>
            <Reveal>
              <h2 className="display text-2xl sm:text-3xl text-ink">What this sector can expect</h2>
              <p className="mt-5 text-slate-600 leading-relaxed">{ind.intro}</p>
            </Reveal>
            <Reveal delay={0.08}>
              <ul className="mt-8 border-t border-slate-200">
                {ind.points.map((p) => (
                  <li key={p} className="flex items-start gap-3 py-3.5 border-b border-slate-200 text-slate-700">
                    <Check size={16} className="text-signal mt-0.5 shrink-0" /> {p}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
          <div>
            <Reveal delay={0.05}><SpotlightImage src={ind.image} alt={ind.alt} ratio="aspect-[4/3]" /></Reveal>
            <Reveal delay={0.1}>
              <div className="bg-ink text-white p-7 mt-8">
                <p className="eyebrow text-signal">Start the conversation</p>
                <p className="mt-3 text-sm text-slate-300 leading-relaxed">
                  Describe the mission and the instruments involved. You'll receive a responsive, procurement-ready answer —
                  typically within one business day.
                </p>
                <a href={`mailto:${SITE.email}?subject=${encodeURIComponent('Requirement: ' + ind.name)}`} className="btn-light w-full mt-5" data-testid="industry-cta-button">Discuss a Requirement</a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="section-pad bg-slate-50 hairline-t">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow text-signal mb-3">RFQ</p>
            <h2 className="display text-3xl sm:text-4xl text-ink mb-8">Submit a requirement</h2>
          </Reveal>
          <RFQForm defaultNeed="" />
        </div>
      </section>

      <section className="section-pad hairline-t">
        <div className="container-x">
          <h2 className="eyebrow text-signal mb-6">Commonly sourced for this sector</h2>
          <div className="flex flex-wrap gap-2">
            {equipment.map((e) => (
              <Link key={e.slug} to={`/equipment/${e.slug}`} className="border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 hover:text-signal hover:border-signal transition-colors" data-testid={`industry-related-equipment-${e.slug}`}>
                {e.name} <ArrowRight size={12} className="inline ml-1" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
